import type { MovementStatus, ReturnAddress } from "@nexxt/contracts/logistics";
import { m } from "../paraglide/messages.js";

// A vendor return in the words of the screens (pure, with its tests): its status as Tomorrow and as the supplier read
// it, who it waits on, and the address the goods go back to. `isInternal` chooses the words (ADR-169 point 6): the
// supplier reads its own turn where Tomorrow reads the supplier's.

/** The statuses a vendor return takes (ADR-227 point 3): the list filters on these. */
export const vendorReturnStatuses = [
  "requested",
  "awaiting_confirmation",
  "confirmed",
  "executed",
  "cancelled",
] as const satisfies readonly MovementStatus[];
type VendorReturnStatus = (typeof vendorReturnStatuses)[number];

const internalLabels: Record<VendorReturnStatus, () => string> = {
  requested: m.vendor_return_status_requested,
  awaiting_confirmation: m.vendor_return_status_refused,
  confirmed: m.vendor_return_status_confirmed,
  executed: m.vendor_return_status_executed,
  cancelled: m.vendor_return_status_cancelled,
};

const externalLabels: Record<VendorReturnStatus, () => string> = {
  ...internalLabels,
  requested: m.vendor_return_status_requested_own,
  awaiting_confirmation: m.vendor_return_status_refused_own,
};

const isVendorReturnStatus = (status: MovementStatus): status is VendorReturnStatus =>
  (vendorReturnStatuses as readonly MovementStatus[]).includes(status);

/** The status in words; a status a vendor return never takes keeps its code. */
export function vendorReturnStatusLabel(status: MovementStatus, isInternal: boolean): string {
  if (!isVendorReturnStatus(status)) return status;
  return (isInternal ? internalLabels : externalLabels)[status]();
}

export interface WaitingVendorReturn {
  status: MovementStatus;
  requestedAt: string | null;
  confirmedAt: string | null;
  supplierRefusedAt: string | null;
  shippedAt: string | null;
  /** Tomorrow's: absent for the supplier. */
  cancelRequestedAt?: string | null | undefined;
  wmsSyncStatus?: string | undefined;
  bcSyncStatus?: string | undefined;
}

type Waiting = "supplier" | "operations" | "cancellation" | "resend" | "shipment";

/**
 * Who a vendor return waits on, and since when (ADR-227 point 3): the supplier while it is `requested`; Operations
 * once the supplier refused it; then, confirmed, the two systems for a cancellation, Logistics for a message refused,
 * the warehouse until it ships. Nothing once shipped or cancelled. The last three the supplier reads only as the
 * shipment it waits for, since the states towards the systems are Tomorrow's.
 */
export function vendorReturnWaiting(vr: WaitingVendorReturn): { on: Waiting; since: string | null } | null {
  if (vr.status === "requested") return { on: "supplier", since: vr.requestedAt };
  if (vr.status === "awaiting_confirmation") return { on: "operations", since: vr.supplierRefusedAt };
  if (vr.status !== "confirmed") return null;
  if (vr.cancelRequestedAt) return { on: "cancellation", since: vr.cancelRequestedAt };
  if (vr.wmsSyncStatus === "sync_failed" || vr.bcSyncStatus === "sync_failed") {
    return { on: "resend", since: vr.confirmedAt };
  }
  return vr.shippedAt === null ? { on: "shipment", since: vr.confirmedAt } : null;
}

const internalWaiting: Record<Waiting, (p: { since: string }) => string> = {
  supplier: m.vendor_return_waiting_on_supplier,
  operations: m.vendor_return_waiting_on_operations,
  cancellation: m.vendor_return_waiting_on_cancellation,
  resend: m.vendor_return_waiting_on_resend,
  shipment: m.vendor_return_waiting_on_shipment,
};

const externalWaiting: Record<Waiting, (p: { since: string }) => string> = {
  ...internalWaiting,
  supplier: m.vendor_return_waiting_on_you,
  operations: m.vendor_return_waiting_on_tomorrow,
};

/** Who the vendor return waits on, in words; `age` says how long ago a moment was. */
export function vendorReturnWaitingOn(
  vr: WaitingVendorReturn,
  isInternal: boolean,
  age: (at: string) => string,
): string | null {
  const waiting = vendorReturnWaiting(vr);
  if (!waiting) return null;
  const since = waiting.since === null ? "—" : age(waiting.since);
  return (isInternal ? internalWaiting : externalWaiting)[waiting.on]({ since });
}

/** The address the goods go back to, one line per part, as the warehouse prints it (I-10 `shipTo`). */
export function returnAddressLines(address: ReturnAddress): string[] {
  const place = [address.postalCode, address.city, address.region].filter(Boolean).join(" ");
  return [address.name, address.line1, address.line2, place, address.country].filter(
    (part): part is string => typeof part === "string" && part.trim() !== "",
  );
}

/** The value of the units of a line, in the currency of the return: `null` without a price. */
export function lineAmount(qty: number, unitPrice: string | null): string | null {
  if (unitPrice === null) return null;
  return (Math.round(qty * Number(unitPrice) * 100) / 100).toFixed(2);
}
